import React, { useState, useEffect } from 'react';
import { apiCall, ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FaRobot, 
  FaCog,
  FaDatabase,
  FaBrain
} from 'react-icons/fa';

interface AIProvider {
  id: string;
  name: string;
  type: 'local' | 'api' | 'mcp';
  description: string;
  specialization?: string;
  status: 'available' | 'configured' | 'offline';
  icon: string;
  pricing?: {
    input?: number;
    output?: number;
    per_minute?: number;
  };
}

interface LetterGenerationRequest {
  patientId: string;
  patientName: string;
  patientDob: string;
  clinicianId: string;
  clinicianName: string;
  letterType: string;
  content: string;
  researchContext?: string[];
  aiProvider: string;
  tone: 'professional' | 'compassionate' | 'technical';
  letterDate: string;
}

const AIIntegrationHub: React.FC = () => {
  const { token } = useAuth();
  
  // Helper to get provider icon from model ID
  const getProviderIconFromModel = (modelId: string): string => {
    const provider = aiProviders.find(p => p.id === modelId);
    return provider?.icon || 'openai'; // Default to openai
  };
  
  // Helper to get smaller badge for inline display
  const getSmallBadge = (icon: string) => {
    switch (icon) {
      case 'openai':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-4 h-4" style={{ color: '#10A37F' }}>
            <path d="M14.305 7.100625A3.75 3.75 0 0 0 10.375 2.08875 3.75 3.75 0 0 0 4.06875 2.989375a3.75 3.75 0 0 0 -2.375 5.91A3.755 3.755 0 0 0 5.225 13.9375a3.614375 3.614375 0 0 0 0.403125 -0.0225 3.75 3.75 0 0 0 6.303125 -0.904375 3.75 3.75 0 0 0 2.375 -5.91ZM11.363125 3.64375a2.25 2.25 0 0 1 1.66125 2.35L10.625 4.608125a0.75 0.75 0 0 0 -0.75 0l-2.375 1.37125V5.113125l2.15625 -1.244375a2.23375 2.23375 0 0 1 1.706875 -0.225ZM8.5 7.71125v0.5775l-0.5 0.28875 -0.5 -0.28875v-0.5775l0.5 -0.28875ZM5.25 4.5a2.25 2.25 0 0 1 3.524375 -1.854375L6.375 4.030625a0.75 0.75 0 0 0 -0.375 0.649375v2.7425l-0.75 -0.433125ZM2.770625 5.558125A2.238125 2.238125 0 0 1 3.75 4.651875v2.770625a0.75 0.75 0 0 0 0.375 0.65l2.375 1.370625L5.75 9.875l-2.15625 -1.24375a2.25 2.25 0 0 1 -0.823125 -3.073125ZM4.636875 12.35625a2.25 2.25 0 0 1 -1.66125 -2.35L5.375 11.391875a0.75 0.75 0 0 0 0.75 0l2.375 -1.37125v0.86625l-2.15625 1.244375a2.23375 2.23375 0 0 1 -1.706875 0.225ZM10.75 11.5a2.25 2.25 0 0 1 -3.524375 1.854375L9.625 11.969375a0.75 0.75 0 0 0 0.375 -0.649375v-2.7425l0.75 0.433125Zm2.479375 -1.058125a2.238125 2.238125 0 0 1 -0.979375 0.90625v-2.770625a0.75 0.75 0 0 0 -0.375 -0.65l-2.375 -1.370625L10.25 6.125l2.15625 1.245a2.25 2.25 0 0 1 0.823125 3.071875Z" strokeWidth="0.0625"></path>
          </svg>
        );
      case 'anthropic':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="w-4 h-4" style={{ fill: '#D97757' }}>
            <path fill="currentColor" d="m13.788825 3.932 6.43325 16.136075h3.5279L17.316725 3.932H13.788825Z" strokeWidth="0.25"></path>
            <path fill="currentColor" d="m6.325375 13.682775 2.20125 -5.67065 2.201275 5.67065H6.325375ZM6.68225 3.932 0.25 20.068075h3.596525l1.3155 -3.3886h6.729425l1.315275 3.3886h3.59655L10.371 3.932H6.68225Z" strokeWidth="0.25"></path>
          </svg>
        );
      case 'ollama':
        return (
          <span className="text-sm">🦙</span>
        );
      default:
        return (
          <FaRobot className="w-4 h-4 text-base-content" />
        );
    }
  };
  
  // Provider badge renderer
  const getProviderBadge = (icon: string) => {
    switch (icon) {
      case 'openai':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-6 h-6" style={{ color: '#10A37F' }}>
            <path d="M14.305 7.100625A3.75 3.75 0 0 0 10.375 2.08875 3.75 3.75 0 0 0 4.06875 2.989375a3.75 3.75 0 0 0 -2.375 5.91A3.755 3.755 0 0 0 5.225 13.9375a3.614375 3.614375 0 0 0 0.403125 -0.0225 3.75 3.75 0 0 0 6.303125 -0.904375 3.75 3.75 0 0 0 2.375 -5.91ZM11.363125 3.64375a2.25 2.25 0 0 1 1.66125 2.35L10.625 4.608125a0.75 0.75 0 0 0 -0.75 0l-2.375 1.37125V5.113125l2.15625 -1.244375a2.23375 2.23375 0 0 1 1.706875 -0.225ZM8.5 7.71125v0.5775l-0.5 0.28875 -0.5 -0.28875v-0.5775l0.5 -0.28875ZM5.25 4.5a2.25 2.25 0 0 1 3.524375 -1.854375L6.375 4.030625a0.75 0.75 0 0 0 -0.375 0.649375v2.7425l-0.75 -0.433125ZM2.770625 5.558125A2.238125 2.238125 0 0 1 3.75 4.651875v2.770625a0.75 0.75 0 0 0 0.375 0.65l2.375 1.370625L5.75 9.875l-2.15625 -1.24375a2.25 2.25 0 0 1 -0.823125 -3.073125ZM4.636875 12.35625a2.25 2.25 0 0 1 -1.66125 -2.35L5.375 11.391875a0.75 0.75 0 0 0 0.75 0l2.375 -1.37125v0.86625l-2.15625 1.244375a2.23375 2.23375 0 0 1 -1.706875 0.225ZM10.75 11.5a2.25 2.25 0 0 1 -3.524375 1.854375L9.625 11.969375a0.75 0.75 0 0 0 0.375 -0.649375v-2.7425l0.75 0.433125Zm2.479375 -1.058125a2.238125 2.238125 0 0 1 -0.979375 0.90625v-2.770625a0.75 0.75 0 0 0 -0.375 -0.65l-2.375 -1.370625L10.25 6.125l2.15625 1.245a2.25 2.25 0 0 1 0.823125 3.071875Z" strokeWidth="0.0625"></path>
          </svg>
        );
      case 'anthropic':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="w-6 h-6" style={{ fill: '#D97757' }}>
            <path fill="currentColor" d="m13.788825 3.932 6.43325 16.136075h3.5279L17.316725 3.932H13.788825Z" strokeWidth="0.25"></path>
            <path fill="currentColor" d="m6.325375 13.682775 2.20125 -5.67065 2.201275 5.67065H6.325375ZM6.68225 3.932 0.25 20.068075h3.596525l1.3155 -3.3886h6.729425l1.315275 3.3886h3.59655L10.371 3.932H6.68225Z" strokeWidth="0.25"></path>
          </svg>
        );
      case 'ollama':
        return (
          <div className="w-6 h-6 flex items-center justify-center">
            <span className="text-lg">🦙</span>
          </div>
        );
      default:
        return (
          <div className="w-6 h-6 flex items-center justify-center">
            <FaRobot className="text-base-content" />
          </div>
        );
    }
  };
  
  // State management
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // AI Providers state
  const [aiProviders, setAiProviders] = useState<AIProvider[]>([]);
  const [selectedProviderFilter, setSelectedProviderFilter] = useState<string>('all');
  
  // Configuration state
  const [transcriptionModel, setTranscriptionModel] = useState<string>('whisper-1');
  const [researchModel, setResearchModel] = useState<string>('gpt-4o-mini');
  const [temperature, setTemperature] = useState<number>(0.7);
  const [maxTokens, setMaxTokens] = useState<number>(2000);
  
  // Dropdown visibility state
  const [showTranscriptionDropdown, setShowTranscriptionDropdown] = useState(false);
  const [showLetterDropdown, setShowLetterDropdown] = useState(false);
  const [showResearchDropdown, setShowResearchDropdown] = useState(false);
  
  // Letter generation state - keeping minimal for model selection
  const [letterRequest, setLetterRequest] = useState<LetterGenerationRequest>({
    patientId: '',
    patientName: '',
    patientDob: '',
    clinicianId: '',
    clinicianName: '',
    letterType: 'referral',
    content: '',
    aiProvider: 'gpt-4o-mini',  // Default to GPT-4o-mini
    tone: 'professional',
    letterDate: new Date().toLocaleDateString('en-AU')
  });

  // Load saved configuration on mount
  useEffect(() => {
    const loadConfig = async () => {
      if (!token) return;
      
      try {
        const response = await apiCall(ENDPOINTS.AI.GET_CONFIG, {
          method: 'GET'
        }, token);
        
        if (response.ok) {
          const config = await response.json();
          setTranscriptionModel(config.transcription_model || 'whisper-1');
          setResearchModel(config.research_assistant_model || 'gpt-4o-mini');
          setTemperature(config.api_config?.temperature || 0.7);
          setMaxTokens(config.api_config?.max_tokens || 2000);
          
          // Update letter request with saved model
          if (config.letter_generation_model) {
            setLetterRequest(prev => ({
              ...prev,
              aiProvider: config.letter_generation_model
            }));
          }
        }
      } catch (error) {
        console.error('Failed to load configuration:', error);
      }
    };
    
    loadConfig();
  }, [token]);
  
  // Load available AI models on component mount
  useEffect(() => {
    const loadAIModels = async () => {
      if (!token) return;
      
      try {
        // Fetch discovered models from OpenAI and Ollama
        const response = await apiCall(ENDPOINTS.AI.DISCOVER_MODELS, {
          method: 'GET'
        }, token);
        
        if (response.ok) {
          const data = await response.json();
          const models = data.models || [];
          
          // Build AI providers list from discovered models
          const providers: AIProvider[] = models.map((model: any) => ({
            id: model.id,
            name: model.name,
            type: model.type,
            description: model.description,
            specialization: model.capabilities?.join(', ') || 'General AI Model',
            status: model.status as 'available' | 'configured' | 'offline',
            icon: model.icon
          }));
          
          setAiProviders(providers);
          
          // Set default provider if available
          if (providers.length > 0 && !letterRequest.aiProvider) {
            // Prefer GPT-4o-mini as default
            const defaultProvider = providers.find(p => p.id === 'gpt-4o-mini') || providers[0];
            setLetterRequest(prev => ({
              ...prev,
              aiProvider: defaultProvider.id
            }));
          }
        } else {
          console.error('Failed to fetch AI models');
          // Fallback to basic models
          setAiProviders([
            {
              id: 'gpt-4o-mini',
              name: 'GPT-4o Mini',
              type: 'api',
              description: 'Fast, efficient OpenAI model optimized for medical documentation',
              specialization: 'Medical Letters & Reports',
              status: 'available',
              icon: 'openai'
            }
          ]);
        }
      } catch (error) {
        console.error('Error loading AI models:', error);
        // Fallback to default models
        setAiProviders([
          {
            id: 'gpt-4o-mini',
            name: 'GPT-4o Mini',
            type: 'api',
            description: 'Fast, efficient OpenAI model optimized for medical documentation',
            specialization: 'Medical Letters & Reports',
            status: 'available',
            icon: 'openai'
          },
          {
            id: 'whisper-1',
            name: 'Whisper (Transcription)',
            type: 'api',
            description: 'OpenAI Whisper for audio transcription',
            specialization: 'Audio Transcription',
            status: 'available',
            icon: 'openai'
          }
        ]);
      }
    };

    loadAIModels();
  }, [token]);
  
  const handleSaveConfiguration = async () => {
    if (!token) return;
    
    setIsSaving(true);
    setError(null);
    
    try {
      const config = {
        transcription_model: transcriptionModel,
        letter_generation_model: letterRequest.aiProvider,
        research_assistant_model: researchModel,
        api_config: {
          temperature: temperature,
          max_tokens: maxTokens
        }
      };
      
      const response = await apiCall(ENDPOINTS.AI.SAVE_CONFIG, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      }, token);
      
      if (response.ok) {
        const data = await response.json();
        // Show success message (you might want to use a toast notification here)
        alert('Configuration saved successfully!');
      } else {
        const error = await response.json();
        setError(error.message || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Error saving configuration:', error);
      setError('An error occurred while saving configuration');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleRefreshModels = async () => {
    if (!token) return;
    
    try {
      const response = await apiCall(ENDPOINTS.AI.DISCOVER_MODELS, {
        method: 'GET'
      }, token);
      
      if (response.ok) {
        const data = await response.json();
        const models = data.models || [];
        
        const providers: AIProvider[] = models.map((model: any) => ({
          id: model.id,
          name: model.name,
          type: model.type,
          description: model.description,
          specialization: model.capabilities?.join(', ') || 'General AI Model',
          status: model.status as 'available' | 'configured' | 'offline',
          icon: model.icon,
          pricing: model.pricing
        }));
        
        setAiProviders(providers);
        alert('Models refreshed successfully!');
      }
    } catch (error) {
      console.error('Error refreshing models:', error);
      setError('Failed to refresh models');
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
          <FaBrain className="text-primary" />
          AI Integration Hub
        </h1>
      </div>

      {error && (
        <div className="alert alert-error mb-6">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {/* AI Configuration Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* AI Providers Configuration */}
        <div className="card bg-base-200 shadow-xl">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <h2 className="card-title text-base-content">
                <FaCog className="text-warning" />
                Available AI List
              </h2>
              
              {/* Provider Filter Dropdown */}
              <select
                className="select select-bordered w-48"
                value={selectedProviderFilter}
                onChange={(e) => setSelectedProviderFilter(e.target.value)}
              >
                <option value="all">All Providers</option>
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic</option>
                <option value="ollama">Ollama</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            {/* Scrollable Provider List */}
            <div className="space-y-2 overflow-y-auto max-h-96">
              {aiProviders
                .filter(provider => {
                  if (selectedProviderFilter === 'all') return true;
                  const providerType = provider.type.toLowerCase();
                  const providerName = provider.name.toLowerCase();
                  if (selectedProviderFilter === 'openai') return providerName.includes('gpt') || providerName.includes('whisper') || providerName.includes('openai');
                  if (selectedProviderFilter === 'anthropic') return providerName.includes('claude') || providerName.includes('anthropic');
                  if (selectedProviderFilter === 'ollama') return providerType === 'local' || providerName.includes('ollama') || providerName.includes('llama');
                  return true;
                })
                .map(provider => (
                <div key={provider.id} className="flex items-center gap-3 p-3 bg-base-100 rounded-lg hover:bg-base-200 transition-colors">
                  <div className="flex-shrink-0">
                    {getProviderBadge(provider.icon)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm">{provider.name}</h4>
                    <p className="text-xs opacity-70">{provider.description}</p>
                    {provider.pricing && (
                      <p className="text-xs opacity-60 mt-1">
                        {provider.pricing.per_minute ? (
                          `💲 $${provider.pricing.per_minute}/min`
                        ) : (
                          `💲 In: $${provider.pricing.input}/M · Out: $${provider.pricing.output}/M tokens`
                        )}
                      </p>
                    )}
                  </div>
                  <div className="text-xs opacity-70 max-w-xs truncate">
                    {provider.specialization}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* AI Configuration */}
        <div className="card bg-base-200 shadow-xl">
            <div className="card-body">
              <h2 className="card-title text-base-content">
                <FaDatabase className="text-info" />
                AI Configuration
              </h2>
              
              <div className="space-y-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">Transcription Model</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex-shrink-0">
                      {getProviderBadge(getProviderIconFromModel(transcriptionModel))}
                    </div>
                    <div className="dropdown dropdown-bottom w-full" onBlur={() => setTimeout(() => setShowTranscriptionDropdown(false), 200)}>
                      <div 
                        tabIndex={0} 
                        role="button" 
                        className="btn btn-bordered w-full justify-start bg-base-100 hover:bg-base-200 normal-case font-normal"
                        onClick={() => setShowTranscriptionDropdown(!showTranscriptionDropdown)}
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {getSmallBadge(getProviderIconFromModel(transcriptionModel))}
                          <span className="truncate">{aiProviders.find(p => p.id === transcriptionModel)?.name || 'Whisper (Transcription)'}</span>
                        </div>
                      </div>
                      {showTranscriptionDropdown && (
                        <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-full mt-1 max-h-80 overflow-x-hidden" style={{ overflowY: 'scroll', scrollbarWidth: 'thin' }}>
                          {aiProviders
                            .filter(p => p.specialization?.includes('Transcription') || p.id.includes('whisper'))
                            .map(provider => (
                              <li key={provider.id} className="w-full">
                                <a onClick={() => { setTranscriptionModel(provider.id); setShowTranscriptionDropdown(false); }} className="flex items-center gap-2 w-full min-w-0">
                                  <div className="flex-shrink-0">{getSmallBadge(provider.icon)}</div>
                                  <span className="truncate flex-1">{provider.name}</span>
                                  {provider.pricing?.per_minute && <span className="text-xs opacity-60 flex-shrink-0">($${provider.pricing.per_minute}/min)</span>}
                                </a>
                              </li>
                            ))}
                          {aiProviders.filter(p => p.specialization?.includes('Transcription') || p.id.includes('whisper')).length === 0 && (
                            <li className="w-full">
                              <a onClick={() => { setTranscriptionModel('whisper-1'); setShowTranscriptionDropdown(false); }} className="flex items-center gap-2 w-full">
                                <div className="flex-shrink-0">{getSmallBadge('openai')}</div>
                                <span className="truncate">Whisper (Transcription)</span>
                              </a>
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                  </div>
                  <label className="label">
                    <span className="label-text-alt">✅ Active for audio dictation transcription</span>
                  </label>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">Letter Generation Model</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex-shrink-0">
                      {getProviderBadge(getProviderIconFromModel(letterRequest.aiProvider))}
                    </div>
                    <div className="dropdown dropdown-bottom w-full" onBlur={() => setTimeout(() => setShowLetterDropdown(false), 200)}>
                      <div 
                        tabIndex={0} 
                        role="button" 
                        className="btn btn-bordered w-full justify-start bg-base-100 hover:bg-base-200 normal-case font-normal"
                        onClick={() => setShowLetterDropdown(!showLetterDropdown)}
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {getSmallBadge(getProviderIconFromModel(letterRequest.aiProvider))}
                          <span className="truncate">{aiProviders.find(p => p.id === letterRequest.aiProvider)?.name || 'GPT-4o Mini'}</span>
                        </div>
                      </div>
                      {showLetterDropdown && (
                        <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-full mt-1 max-h-80 overflow-x-hidden" style={{ overflowY: 'scroll', scrollbarWidth: 'thin' }}>
                          {aiProviders
                            .filter(p => !p.id.includes('whisper') && (p.specialization?.includes('Letter') || p.specialization?.includes('Medical') || p.id.includes('gpt') || p.id.includes('claude')))
                            .map(provider => (
                              <li key={provider.id} className="w-full">
                                <a onClick={() => { setLetterRequest({ ...letterRequest, aiProvider: provider.id }); setShowLetterDropdown(false); }} className="flex items-center gap-2 w-full min-w-0">
                                  <div className="flex-shrink-0">{getSmallBadge(provider.icon)}</div>
                                  <span className="truncate flex-1">{provider.name}</span>
                                  {provider.pricing?.input && <span className="text-xs opacity-60 flex-shrink-0">($${provider.pricing.input}/$${provider.pricing.output}/M)</span>}
                                </a>
                              </li>
                            ))}
                          {aiProviders.filter(p => !p.id.includes('whisper')).length === 0 && (
                            <li className="w-full">
                              <a onClick={() => { setLetterRequest({ ...letterRequest, aiProvider: 'gpt-4o-mini' }); setShowLetterDropdown(false); }} className="flex items-center gap-2 w-full">
                                <div className="flex-shrink-0">{getSmallBadge('openai')}</div>
                                <span className="truncate">GPT-4o Mini</span>
                              </a>
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                  </div>
                  <label className="label">
                    <span className="label-text-alt">Default model for AI letter generation</span>
                  </label>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">Research Assistant Model</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex-shrink-0">
                      {getProviderBadge(getProviderIconFromModel(researchModel))}
                    </div>
                    <div className="dropdown dropdown-bottom w-full" onBlur={() => setTimeout(() => setShowResearchDropdown(false), 200)}>
                      <div 
                        tabIndex={0} 
                        role="button" 
                        className="btn btn-bordered w-full justify-start bg-base-100 hover:bg-base-200 normal-case font-normal"
                        onClick={() => setShowResearchDropdown(!showResearchDropdown)}
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {getSmallBadge(getProviderIconFromModel(researchModel))}
                          <span className="truncate">{aiProviders.find(p => p.id === researchModel)?.name || 'GPT-4o Mini'}</span>
                        </div>
                      </div>
                      {showResearchDropdown && (
                        <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-full mt-1 max-h-80 overflow-x-hidden" style={{ overflowY: 'scroll', scrollbarWidth: 'thin' }}>
                          {aiProviders
                            .filter(p => !p.id.includes('whisper') && (p.id.includes('gpt') || p.id.includes('claude') || p.id.includes('llama')))
                            .map(provider => (
                              <li key={provider.id} className="w-full">
                                <a onClick={() => { setResearchModel(provider.id); setShowResearchDropdown(false); }} className="flex items-center gap-2 w-full min-w-0">
                                  <div className="flex-shrink-0">{getSmallBadge(provider.icon)}</div>
                                  <span className="truncate flex-1">{provider.name}</span>
                                  {provider.pricing?.input && <span className="text-xs opacity-60 flex-shrink-0">($${provider.pricing.input}/$${provider.pricing.output}/M)</span>}
                                </a>
                              </li>
                            ))}
                          {aiProviders.filter(p => !p.id.includes('whisper')).length === 0 && (
                            <li className="w-full">
                              <a onClick={() => { setResearchModel('gpt-4o-mini'); setShowResearchDropdown(false); }} className="flex items-center gap-2 w-full">
                                <div className="flex-shrink-0">{getSmallBadge('openai')}</div>
                                <span className="truncate">GPT-4o Mini</span>
                              </a>
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                  </div>
                  <label className="label">
                    <span className="label-text-alt">Model used for PubMed research filtering and analysis</span>
                  </label>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">API Configuration</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="label">
                        <span className="label-text-alt">Temperature</span>
                      </label>
                      <input
                        type="number"
                        className="input input-bordered input-sm w-full"
                        placeholder="0.7"
                        min="0"
                        max="2"
                        step="0.1"
                        value={temperature}
                        onChange={(e) => setTemperature(parseFloat(e.target.value))}
                      />
                    </div>
                    <div>
                      <label className="label">
                        <span className="label-text-alt">Max Tokens</span>
                      </label>
                      <input
                        type="number"
                        className="input input-bordered input-sm w-full"
                        placeholder="2000"
                        value={maxTokens}
                        onChange={(e) => setMaxTokens(parseInt(e.target.value))}
                      />
                    </div>
                  </div>
                </div>

                <div className="card-actions justify-end">
                  <button 
                    className="btn btn-outline" 
                    onClick={handleRefreshModels}
                    disabled={isSaving}
                  >
                    Refresh Models
                  </button>
                  <button 
                    className={`btn btn-primary ${isSaving ? 'loading' : ''}`}
                    onClick={handleSaveConfiguration}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save Configuration'}
                  </button>
                </div>
              </div>
            </div>
          </div>
      </div>
    </div>
  );
};

export default AIIntegrationHub;